import type { ApiSettingsResponse } from "../models/api/admin/apiSettings";
import { useGetApiSettings } from "../api/admin/apiSettings";
import { useEffect } from "react";
import useSessionStorage from "./useSessionStorage";
export const API_SETTINGS_STORAGE_KEY = "apiSettings";

export type DateHighlightStyle = "none" | "alert" | "warning";

export const useDueToExpireSettings = () => {
    const [storedDueToExpireSettings, setStoredApiSettings] =
        useSessionStorage<ApiSettingsResponse | null>(
            API_SETTINGS_STORAGE_KEY,
            null
        );

    const skipFetch = storedDueToExpireSettings !== null;
    const { apiSettings } = useGetApiSettings(skipFetch);    

    type DateTypeKey = keyof ApiSettingsResponse;

    const isDateDueToExpire = (date: string, dateType: DateTypeKey) => {
        if (!storedDueToExpireSettings) return false;
        const dueDate = new Date(date);
        const today = new Date();
        const diffTime = Math.abs(dueDate.getTime() - today.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // Convert months to days if the setting is in months
        const threshold = dateType.includes("Months")
            ? (storedDueToExpireSettings[dateType] ?? 0) * 30
            : storedDueToExpireSettings[dateType] ?? 0;

        return diffDays <= threshold;
    };

    const getDateHighlightStyle = (
        date: string,
        dateType: DateTypeKey
    ): DateHighlightStyle => {
        if (!date) return "none";

        const isExpired = new Date(date) < new Date();
        const isExpiring = isDateDueToExpire(date, dateType);

        if (isExpired) return "alert";
        if (isExpiring) return "warning";
        return "none";
    };

    useEffect(() => {
        if (apiSettings) {
            setStoredApiSettings(apiSettings);
        }
    }, [apiSettings]);

    return {
        storedDueToExpireSettings,
        isDateDueToExpire,
        getDateHighlightStyle,
    };
};
